import { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle
} from '../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { format } from 'date-fns';
import {
  GitBranch, Plus, Loader2, RefreshCw, Edit2, Trash2, CheckCircle2, Scale,
  Users, Clock, ArrowRight, ChevronDown, ChevronUp, Copy, Settings2
} from 'lucide-react';
import api from '../api';

const stageTypeConfig = {
  draft: { label: 'Draft', icon: Edit2, color: 'bg-slate-100 text-slate-700' },
  review: { label: 'Review', icon: Users, color: 'bg-blue-100 text-blue-700' },
  legal_review: { label: 'Legal Review', icon: Scale, color: 'bg-purple-100 text-purple-700' },
  approval: { label: 'Approval', icon: CheckCircle2, color: 'bg-green-100 text-green-700' },
  scheduling: { label: 'Scheduling', icon: Clock, color: 'bg-amber-100 text-amber-700' },
  launch: { label: 'Launch', icon: ArrowRight, color: 'bg-emerald-100 text-emerald-700' },
  revision_required: { label: 'Revision Required', icon: Edit2, color: 'bg-orange-100 text-orange-700' },
  rejected: { label: 'Rejected', icon: Trash2, color: 'bg-red-100 text-red-700' }
};

export const WorkflowsPage = () => {
  const { hasRole } = useAuth();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [workflows, setWorkflows] = useState([]);
  const [expandedWorkflow, setExpandedWorkflow] = useState(null);
  
  // Dialog states
  const [showWorkflowDialog, setShowWorkflowDialog] = useState(false);
  const [showStageDialog, setShowStageDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [editingWorkflow, setEditingWorkflow] = useState(null);
  const [deletingWorkflow, setDeletingWorkflow] = useState(null);
  const [saving, setSaving] = useState(false);
  
  const [workflowForm, setWorkflowForm] = useState({
    name: '',
    description: '',
    workflow_type: 'campaign',
    require_legal_review: true,
    legal_score_threshold: 90,
    stages: []
  });

  const [stageForm, setStageForm] = useState({
    name: '',
    stage_type: 'review',
    approvers: [],
    require_all_approvers: false,
    auto_advance: false,
    time_limit_hours: null,
    failure_action: 'restart',
    failure_go_to_stage_id: null
  });

  const [users, setUsers] = useState([]);

  useEffect(() => {
    fetchWorkflows();
    fetchUsers();
  }, []);

  const fetchWorkflows = async () => {
    try {
      setRefreshing(true);
      const res = await api.get('/marketing/workflows');
      setWorkflows(res.data || []);
    } catch (error) {
      console.error('Failed to fetch workflows:', error);
      toast.error('Failed to load workflows');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const res = await api.get('/users/team');
      setUsers(res.data || []);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  };

  const handleCreateWorkflow = () => {
    setEditingWorkflow(null);
    setWorkflowForm({
      name: '',
      description: '',
      workflow_type: 'campaign',
      require_legal_review: true,
      legal_score_threshold: 90,
      stages: []
    });
    setShowWorkflowDialog(true);
  };

  const handleEditWorkflow = (workflow) => {
    setEditingWorkflow(workflow);
    setWorkflowForm({
      name: workflow.name || '',
      description: workflow.description || '',
      workflow_type: workflow.workflow_type || 'campaign',
      require_legal_review: workflow.require_legal_review ?? true,
      legal_score_threshold: workflow.legal_score_threshold || 90,
      stages: workflow.stages || []
    });
    setShowWorkflowDialog(true);
  };

  const handleSaveWorkflow = async () => {
    if (!workflowForm.name) {
      toast.error('Workflow name is required');
      return;
    }
    
    try {
      setSaving(true);
      
      // Ensure stages have proper order
      const stagesWithOrder = workflowForm.stages.map((stage, idx) => ({
        ...stage,
        order: idx + 1
      }));
      
      const payload = {
        ...workflowForm,
        stages: stagesWithOrder
      };
      
      if (editingWorkflow) {
        await api.put(`/marketing/workflows/${editingWorkflow.id}`, payload);
        toast.success('Workflow updated');
      } else {
        await api.post('/marketing/workflows', payload);
        toast.success('Workflow created');
      }
      
      setShowWorkflowDialog(false);
      fetchWorkflows();
    } catch (error) {
      console.error('Failed to save workflow:', error);
      toast.error('Failed to save workflow');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteWorkflow = async () => {
    if (!deletingWorkflow) return;
    
    try {
      await api.delete(`/marketing/workflows/${deletingWorkflow.id}`);
      toast.success('Workflow deleted');
      setShowDeleteDialog(false);
      setDeletingWorkflow(null);
      fetchWorkflows();
    } catch (error) {
      console.error('Failed to delete workflow:', error);
      toast.error('Failed to delete workflow');
    }
  };

  const handleAddStage = () => {
    setStageForm({
      name: '',
      stage_type: 'review',
      approvers: [],
      require_all_approvers: false,
      auto_advance: false,
      time_limit_hours: null,
      failure_action: 'restart',
      failure_go_to_stage_id: null
    });
    setShowStageDialog(true);
  };

  const handleSaveStage = () => {
    if (!stageForm.name) {
      toast.error('Stage name is required');
      return;
    }
    
    const newStage = {
      id: crypto.randomUUID(),
      ...stageForm,
      order: workflowForm.stages.length + 1
    };
    
    setWorkflowForm({
      ...workflowForm,
      stages: [...workflowForm.stages, newStage]
    });
    setShowStageDialog(false);
  };

  const handleRemoveStage = (stageId) => {
    setWorkflowForm({
      ...workflowForm,
      stages: workflowForm.stages.filter(s => s.id !== stageId)
    });
  };

  const handleMoveStage = (index, direction) => {
    const newStages = [...workflowForm.stages];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    
    if (newIndex < 0 || newIndex >= newStages.length) return;
    
    [newStages[index], newStages[newIndex]] = [newStages[newIndex], newStages[index]];
    setWorkflowForm({ ...workflowForm, stages: newStages });
  };

  const handleDuplicateWorkflow = async (workflow) => {
    try {
      const newWorkflow = {
        name: `${workflow.name} (Copy)`,
        description: workflow.description,
        workflow_type: workflow.workflow_type,
        require_legal_review: workflow.require_legal_review,
        legal_score_threshold: workflow.legal_score_threshold,
        stages: workflow.stages?.map(s => ({ ...s, id: crypto.randomUUID() })) || []
      };
      
      await api.post('/marketing/workflows', newWorkflow);
      toast.success('Workflow duplicated');
      fetchWorkflows();
    } catch (error) {
      console.error('Failed to duplicate workflow:', error);
      toast.error('Failed to duplicate workflow');
    }
  };

  const toggleWorkflowActive = async (workflow) => {
    try {
      await api.put(`/marketing/workflows/${workflow.id}`, {
        is_active: !workflow.is_active
      });
      toast.success(`Workflow ${workflow.is_active ? 'deactivated' : 'activated'}`);
      fetchWorkflows();
    } catch (error) {
      console.error('Failed to toggle workflow:', error);
      toast.error('Failed to update workflow');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="workflows-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <GitBranch className="h-6 w-6" />
            Approval Workflows
          </h1>
          <p className="text-muted-foreground">
            Configure approval workflows for campaigns and content
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={fetchWorkflows} disabled={refreshing}>
            {refreshing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
            Refresh
          </Button>
          {hasRole(['admin', 'marketing_manager']) && (
            <Button onClick={handleCreateWorkflow} data-testid="create-workflow-btn">
              <Plus className="h-4 w-4 mr-2" />
              Create Workflow
            </Button>
          )}
        </div>
      </div>

      {/* Workflows List */}
      {workflows.length === 0 ? (
        <Card>
          <CardContent className="py-16">
            <div className="text-center text-muted-foreground">
              <GitBranch className="h-16 w-16 mx-auto mb-4 opacity-50" />
              <h3 className="text-lg font-medium mb-2">No Workflows Yet</h3>
              <p className="mb-4">Create your first approval workflow to streamline campaign reviews</p>
              {hasRole(['admin', 'marketing_manager']) && (
                <Button onClick={handleCreateWorkflow}>
                  <Plus className="h-4 w-4 mr-2" />
                  Create Workflow
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {workflows.map((workflow) => (
            <Card key={workflow.id} className={!workflow.is_active ? 'opacity-60' : ''}>
              <CardHeader 
                className="cursor-pointer"
                onClick={() => setExpandedWorkflow(expandedWorkflow === workflow.id ? null : workflow.id)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <GitBranch className="h-5 w-5 text-blue-600" />
                    </div>
                    <div>
                      <CardTitle className="text-lg flex items-center gap-2">
                        {workflow.name}
                        {workflow.is_default && (
                          <Badge variant="outline" className="text-xs">Default</Badge>
                        )}
                        {!workflow.is_active && (
                          <Badge variant="secondary" className="text-xs">Inactive</Badge>
                        )}
                      </CardTitle>
                      <CardDescription>{workflow.description || 'No description'}</CardDescription>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline">{workflow.stages?.length || 0} stages</Badge>
                    {workflow.require_legal_review && (
                      <Badge variant="secondary" className="flex items-center gap-1">
                        <Scale className="h-3 w-3" />
                        Legal Review
                      </Badge>
                    )}
                    {expandedWorkflow === workflow.id ? (
                      <ChevronUp className="h-5 w-5 text-muted-foreground" />
                    ) : (
                      <ChevronDown className="h-5 w-5 text-muted-foreground" />
                    )}
                  </div>
                </div>
              </CardHeader>
              
              {expandedWorkflow === workflow.id && (
                <CardContent className="border-t pt-4">
                  {/* Workflow Stages */}
                  <div className="mb-4">
                    <h4 className="text-sm font-medium mb-3">Workflow Stages</h4>
                    {workflow.stages?.length > 0 ? (
                      <div className="flex items-center gap-2 overflow-x-auto pb-2">
                        {workflow.stages.map((stage, idx) => {
                          const config = stageTypeConfig[stage.stage_type] || stageTypeConfig.review;
                          const Icon = config.icon;
                          return (
                            <div key={stage.id} className="flex items-center">
                              <div className={`flex items-center gap-2 px-3 py-2 rounded-lg ${config.color} min-w-[120px]`}>
                                <Icon className="h-4 w-4" />
                                <span className="text-sm font-medium whitespace-nowrap">{stage.name}</span>
                              </div>
                              {idx < workflow.stages.length - 1 && (
                                <ArrowRight className="h-4 w-4 mx-2 text-muted-foreground" />
                              )}
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground">No stages defined</p>
                    )}
                  </div>
                  
                  {/* Workflow Settings */}
                  <div className="grid grid-cols-2 gap-4 mb-4 text-sm">
                    <div>
                      <span className="text-muted-foreground">Type:</span>
                      <span className="ml-2 capitalize">{workflow.workflow_type}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Legal Score Threshold:</span>
                      <span className="ml-2">{workflow.legal_score_threshold}%</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Created:</span>
                      <span className="ml-2">
                        {workflow.created_at ? format(new Date(workflow.created_at), 'MMM d, yyyy') : 'N/A'}
                      </span>
                    </div>
                  </div>
                  
                  {/* Actions */}
                  {hasRole(['admin', 'marketing_manager']) && (
                    <div className="flex items-center gap-2 pt-2 border-t">
                      <Button variant="outline" size="sm" onClick={() => handleEditWorkflow(workflow)}>
                        <Edit2 className="h-4 w-4 mr-2" />
                        Edit
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => handleDuplicateWorkflow(workflow)}>
                        <Copy className="h-4 w-4 mr-2" />
                        Duplicate
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => toggleWorkflowActive(workflow)}>
                        <Settings2 className="h-4 w-4 mr-2" />
                        {workflow.is_active ? 'Deactivate' : 'Activate'}
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => { setDeletingWorkflow(workflow); setShowDeleteDialog(true); }}
                        className="text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Delete
                      </Button>
                    </div>
                  )}
                </CardContent>
              )}
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Workflow Dialog */}
      <Dialog open={showWorkflowDialog} onOpenChange={setShowWorkflowDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingWorkflow ? 'Edit Workflow' : 'Create Workflow'}
            </DialogTitle>
            <DialogDescription>
              Define the stages and approvers for this workflow
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6">
            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">Workflow Name *</Label>
                <Input
                  id="name"
                  value={workflowForm.name}
                  onChange={(e) => setWorkflowForm({ ...workflowForm, name: e.target.value })}
                  placeholder="e.g., Campaign Approval"
                  data-testid="workflow-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Workflow Type</Label>
                <Select
                  value={workflowForm.workflow_type}
                  onValueChange={(value) => setWorkflowForm({ ...workflowForm, workflow_type: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="campaign">Campaign</SelectItem>
                    <SelectItem value="content">Content</SelectItem>
                    <SelectItem value="ad">Ad</SelectItem>
                    <SelectItem value="email">Email</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={workflowForm.description}
                onChange={(e) => setWorkflowForm({ ...workflowForm, description: e.target.value })}
                placeholder="Describe this workflow..."
                rows={2}
              />
            </div>
            
            {/* Legal Review Settings */}
            <div className="p-4 bg-purple-50 rounded-lg space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Scale className="h-5 w-5 text-purple-600" />
                  <Label>Require Legal Review</Label>
                </div>
                <Switch
                  checked={workflowForm.require_legal_review}
                  onCheckedChange={(checked) => setWorkflowForm({ ...workflowForm, require_legal_review: checked })}
                />
              </div>
              {workflowForm.require_legal_review && (
                <div className="space-y-2">
                  <Label>Legal Score Threshold (%)</Label>
                  <Input
                    type="number"
                    min={0}
                    max={100}
                    value={workflowForm.legal_score_threshold}
                    onChange={(e) => setWorkflowForm({ ...workflowForm, legal_score_threshold: parseInt(e.target.value) || 90 })}
                  />
                  <p className="text-xs text-muted-foreground">
                    Content must score at least this percentage to pass legal review
                  </p>
                </div>
              )}
            </div>
            
            {/* Stages */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label>Workflow Stages</Label>
                <Button variant="outline" size="sm" onClick={handleAddStage}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Stage
                </Button>
              </div>
              
              {workflowForm.stages.length === 0 ? (
                <div className="text-center py-8 border-2 border-dashed rounded-lg text-muted-foreground">
                  <p>No stages defined</p>
                  <p className="text-sm">Click &quot;Add Stage&quot; to create your first stage</p>
                </div>
              ) : (
                <ScrollArea className="max-h-[400px]">
                  <div className="space-y-2 pr-4">
                    {workflowForm.stages.map((stage, idx) => {
                      const config = stageTypeConfig[stage.stage_type] || stageTypeConfig.review;
                      return (
                        <div key={stage.id} className="flex items-center gap-2 p-3 border rounded-lg">
                          <div className="flex items-center gap-2 flex-1">
                            <Badge variant="outline" className="text-xs">
                              {idx + 1}
                            </Badge>
                            <div className={`px-2 py-1 rounded text-xs ${config.color}`}>
                              {config.label}
                            </div>
                            <span className="font-medium">{stage.name}</span>
                            {stage.time_limit_hours && (
                              <span className="text-xs text-muted-foreground">({stage.time_limit_hours}h limit)</span>
                            )}
                          </div>
                          <div className="flex items-center gap-1">
                            <Button 
                              variant="ghost" 
                              size="icon"
                              disabled={idx === 0}
                              onClick={() => handleMoveStage(idx, 'up')}
                            >
                              <ChevronUp className="h-4 w-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="icon"
                              disabled={idx === workflowForm.stages.length - 1}
                              onClick={() => handleMoveStage(idx, 'down')}
                            >
                              <ChevronDown className="h-4 w-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="icon"
                              onClick={() => handleRemoveStage(stage.id)}
                            >
                              <Trash2 className="h-4 w-4 text-red-500" />
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </ScrollArea>
              )}
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowWorkflowDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveWorkflow} disabled={saving} data-testid="save-workflow-btn">
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {editingWorkflow ? 'Save Changes' : 'Create Workflow'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Stage Dialog */}
      <Dialog open={showStageDialog} onOpenChange={setShowStageDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add Stage</DialogTitle>
            <DialogDescription>
              Configure a new workflow stage
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="stage-name">Stage Name *</Label>
              <Input
                id="stage-name"
                value={stageForm.name}
                onChange={(e) => setStageForm({ ...stageForm, name: e.target.value })}
                placeholder="e.g., Manager Approval"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Stage Type</Label>
              <Select
                value={stageForm.stage_type}
                onValueChange={(value) => setStageForm({ ...stageForm, stage_type: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(stageTypeConfig).map(([type, config]) => (
                    <SelectItem key={type} value={type}>{config.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Time Limit (hours)</Label>
              <Input
                type="number"
                value={stageForm.time_limit_hours || ''}
                onChange={(e) => setStageForm({ ...stageForm, time_limit_hours: e.target.value ? parseInt(e.target.value) : null })}
                placeholder="Optional"
              />
            </div>
            
            <div className="flex items-center justify-between">
              <div>
                <Label>Require All Approvers</Label>
                <p className="text-xs text-muted-foreground">All assigned approvers must approve</p>
              </div>
              <Switch
                checked={stageForm.require_all_approvers}
                onCheckedChange={(checked) => setStageForm({ ...stageForm, require_all_approvers: checked })}
              />
            </div>
            
            <div className="flex items-center justify-between">
              <div>
                <Label>Auto-Advance</Label>
                <p className="text-xs text-muted-foreground">Automatically move to next stage when approved</p>
              </div>
              <Switch
                checked={stageForm.auto_advance}
                onCheckedChange={(checked) => setStageForm({ ...stageForm, auto_advance: checked })}
              />
            </div>
            
            <div className="space-y-2 border-t pt-4">
              <Label className="text-orange-600">On Rejection / Failure</Label>
              <Select
                value={stageForm.failure_action}
                onValueChange={(value) => setStageForm({ ...stageForm, failure_action: value, failure_go_to_stage_id: null })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select failure action" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="restart">Restart from Beginning</SelectItem>
                  <SelectItem value="go_to_stage">Go to Specific Stage</SelectItem>
                  <SelectItem value="reject">Reject Campaign Entirely</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">What happens when this stage is rejected</p>
              
              {stageForm.failure_action === 'go_to_stage' && workflowForm.stages.length > 0 && (
                <Select
                  value={stageForm.failure_go_to_stage_id || ''}
                  onValueChange={(value) => setStageForm({ ...stageForm, failure_go_to_stage_id: value })}
                >
                  <SelectTrigger className="mt-2">
                    <SelectValue placeholder="Select stage to return to" />
                  </SelectTrigger>
                  <SelectContent>
                    {workflowForm.stages.map((stage, idx) => (
                      <SelectItem key={stage.id} value={stage.id}>
                        {idx + 1}. {stage.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowStageDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveStage}>
              Add Stage
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Workflow</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete &quot;{deletingWorkflow?.name}&quot;? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteWorkflow} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default WorkflowsPage;
